import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./ChatBot.css";

const initialMessages = [
  {
    sender: "bot",
    text: "Hola, soy el asistente de Whisky & Co. Puedo ayudarte a explorar el catalogo, encontrar el local o iniciar una consulta por WhatsApp.",
  },
];

const quickReplies = [
  { label: "Ver catalogo", value: "catalogo" },
  { label: "Whiskys destacados", value: "destacados" },
  { label: "Ubicacion", value: "ubicacion" },
  { label: "Consultar por WhatsApp", value: "whatsapp" },
];

const buildBotReply = (message) => {
  const text = message.toLowerCase();

  if (text.includes("catalog") || text.includes("producto") || text.includes("whisky")) {
    return {
      text: "El catalogo esta organizado por categorias y tipos de whisky. Podes ver cada ficha, revisar precio orientativo y consultar directamente por WhatsApp desde el detalle.",
      buttons: [
        { label: "Abrir catalogo", value: "go:productos" },
        { label: "Destacados", value: "destacados" },
      ],
    };
  }

  if (text.includes("destacad") || text.includes("promo") || text.includes("promocion")) {
    return {
      text: "Los productos destacados y promociones aparecen en el inicio y en el catalogo. La disponibilidad final se confirma siempre por contacto directo.",
      buttons: [
        { label: "Ver productos", value: "go:productos" },
        { label: "WhatsApp", value: "whatsapp" },
      ],
    };
  }

  if (text.includes("ubic") || text.includes("direccion") || text.includes("local") || text.includes("donde")) {
    return {
      text: "Estamos en Av. Recta Martinolli 8669, Cordoba. Podes acercarte al local o escribirnos para consultar por una etiqueta puntual.",
      buttons: [
        { label: "Ver contacto", value: "go:contacto" },
        { label: "Abrir Maps", value: "mapa" },
      ],
    };
  }

  if (text.includes("whatsapp") || text.includes("contact") || text.includes("asesor") || text.includes("recomend")) {
    return {
      text: "Para recomendaciones o disponibilidad, lo mas directo es escribirnos por WhatsApp. Si ya viste un whisky, mencionalo en el mensaje.",
      buttons: [
        { label: "Abrir WhatsApp", value: "whatsapp" },
        { label: "Ver contacto", value: "go:contacto" },
      ],
    };
  }

  if (text.includes("compra") || text.includes("envio") || text.includes("pago") || text.includes("carrito")) {
    return {
      text: "Esta web funciona como catalogo y presencia digital. No procesa compras online, pagos ni carrito. Las consultas comerciales se coordinan directo con el local.",
      buttons: [
        { label: "Consultar por WhatsApp", value: "whatsapp" },
        { label: "Ver catalogo", value: "go:productos" },
      ],
    };
  }

  return {
    text: "Puedo orientarte sobre catalogo, destacados, ubicacion o contacto. Tambien puedo abrir WhatsApp para que consultes con el equipo.",
    buttons: quickReplies,
  };
};

const ChatBot = () => {
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const quickActionsRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);

  const onDragStart = (e) => {
    isDragging.current = true;
    dragStartX.current = e.pageX - quickActionsRef.current.offsetLeft;
    dragScrollLeft.current = quickActionsRef.current.scrollLeft;
    quickActionsRef.current.classList.add("is-dragging");
  };

  const onDragEnd = () => {
    isDragging.current = false;
    quickActionsRef.current?.classList.remove("is-dragging");
  };

  const onDragMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - quickActionsRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.4;
    quickActionsRef.current.scrollLeft = dragScrollLeft.current - walk;
  };

  const openWhatsApp = () => {
    window.open(
      "https://wa.me/5493515295096?text=Hola,%20quiero%20hacer%20una%20consulta%20sobre%20Whisky%20%26%20Co.",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleAction = (value) => {
    if (value === "go:productos") {
      navigate("/productos");
      setIsOpen(false);
      return;
    }

    if (value === "go:contacto") {
      navigate("/contacto");
      setIsOpen(false);
      return;
    }

    if (value === "mapa") {
      window.open("https://maps.app.goo.gl/GkZN29pu5y3tk7LP7", "_blank", "noopener,noreferrer");
      return;
    }

    if (value === "whatsapp") {
      openWhatsApp();
      return;
    }

    sendMessage(value);
  };

  const sendMessage = (externalInput) => {
    const finalInput = externalInput || input;
    if (finalInput.trim() === "") return;

    const userMessage = { sender: "user", text: finalInput };
    const botReply = { sender: "bot", ...buildBotReply(finalInput) };

    setMessages((prev) => [...prev, userMessage, botReply]);
    setInput("");
  };

  return (
    <>
      <button
        id="chatbot-button"
        className={isOpen ? "is-open" : ""}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Cerrar asistente" : "Abrir asistente"}
      >
        <span>W&Co.</span>
      </button>

      <section id="chat-container" className={isOpen ? "active" : ""} aria-label="Asistente de Whisky & Co">
        <header id="chat-header">
          <div>
            <span className="chat-eyebrow">Asistente de catalogo</span>
            <strong>Whisky & Co</strong>
          </div>
          <button onClick={() => setIsOpen(false)} aria-label="Cerrar asistente">
            Cerrar
          </button>
        </header>

        <div className="chat-quick-actions-wrapper">
          <div
            className="chat-quick-actions"
            ref={quickActionsRef}
            onMouseDown={onDragStart}
            onMouseLeave={onDragEnd}
            onMouseUp={onDragEnd}
            onMouseMove={onDragMove}
          >
            {quickReplies.map((reply) => (
              <button key={reply.value} onClick={() => handleAction(reply.value)}>
                {reply.label}
              </button>
            ))}
          </div>
        </div>

        <div id="chat-box">
          {messages.map((msg, index) => (
            <div key={`${msg.sender}-${index}`} className={`chat-message-row ${msg.sender}`}>
              <p className="chat-bubble">
                {msg.text}
              </p>
              {msg.sender === "bot" && msg.buttons && (
                <div className="bot-buttons">
                  {msg.buttons.map((btn) => (
                    <button
                      key={btn.value}
                      className="bot-button"
                      onClick={() => handleAction(btn.value)}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="chat-input-bar">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Preguntame por catalogo, ubicacion o recomendaciones..."
          />
          <button onClick={() => sendMessage()}>Enviar</button>
        </div>
      </section>
    </>
  );
};

export default ChatBot;
